/**
 * The v2 read model, `ordersV2Store`.
 *
 * v2 owns its reads. Its own actions (`ordersRequested`, `ordersReceived`, `ordersFailed`) carry
 * the `storeKey` in the payload, so ONE action type and ONE saga worker (`./saga`) serve all ten
 * lists including search, where v1 needs nine request/success pairs and its own worker. v1's
 * pairs are untouched and keep filling v1's `*OrdersInfo` fields for v1 web.
 *
 * Every write here comes from v2's own actions. The three flows outside my-orders that refresh
 * these lists (`homeLayout`'s notification poll, `saleHelpers` after an accept,
 * `useCancellationFlow` after a void) go through `./refresh.refreshOrdersList`, which routes to
 * whichever model the terminal is running.
 *
 * Deleting v1 changes nothing here: what goes is v1's slice, its workers, and the
 * `if (!refreshOrdersList(...))` tails at those three call sites.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { stillInList, type StoreKey } from "./buckets";

/**
 * A sale as the LISTS hold it: rista-library's VO (`saleutil.convertSaleToVO`), which is what
 * `sale/filter` returns. Only the fields typed code here reads are declared. The VO is not the raw
 * sale: its key is `saleId` (the raw sale has `mid` only), and its events carry `eventDate` (the
 * raw `eventLog` has `eventBy.date`). `api.ts` converts a raw sale before it reaches this store.
 */
export interface SaleVO {
  saleId: string;
  fulfillmentStatus?: string;
  eventLog?: { status: string; eventDate: string }[];
}

export interface BucketState {
  /** the saleIds of the last response for this list, in server order */
  ids: string[];
  /** a request for this list is in flight */
  fetching: boolean;
}

export interface OrdersV2State {
  /** saleId → the latest copy seen from any list. Holds only ids some bucket references — see `prune`. */
  byId: Record<string, SaleVO>;
  buckets: Record<StoreKey, BucketState>;
}

/** The `sale/filter` response as v1 stores it: `{ results: Order[] }`, or `{}` for an empty read. */
type ListPayload = { results?: SaleVO[] } | undefined;

/** What a v2 list fetch carries: which bucket it fills, plus the request `buckets.listPayload` built. */
export interface OrdersRequest {
  storeKey: StoreKey;
  data?: Record<string, any>;
  /** web's interceptor reads this one … */
  skipGlobalLoader?: boolean;
  /** … mobile's reads this one. Both are set from the same `quiet`. */
  hideLoader?: boolean;
}

/** Every bucket this slice holds: the nine tab lists plus the search results. */
export const BUCKET_KEYS: readonly StoreKey[] = [
  "overviewOrdersInfo",
  "newOrdersInfo",
  "preparingOrdersInfo",
  "preparedOrdersInfo",
  "processedOrdersInfo",
  "dispatchOrdersInfo",
  "deliveredOrdersInfo",
  "cancelledOrdersInfo",
  "openOrdersInfo",
  "searchOrdersInfo",
];

function emptyBuckets(): Record<StoreKey, BucketState> {
  const buckets = {} as Record<StoreKey, BucketState>; // every key is filled right below
  for (const storeKey of BUCKET_KEYS) buckets[storeKey] = { ids: [], fetching: false };
  return buckets;
}

const initialState: OrdersV2State = { byId: {}, buckets: emptyBuckets() };

/**
 * Drop the `byId` rows no bucket references any more.
 *
 * Every list response REPLACES one bucket's ids, so without this the sales that left that list
 * stay in `byId` for the life of the session — a terminal that never reloads accumulates every
 * order it has ever seen, not the ones it can still show. The ids are the reference count: a row
 * survives while ANY bucket lists it (the same sale is routinely in a tab list, the overview and
 * the search hits at once), which is why this cannot be done per bucket.
 *
 * Run from `ordersReceived` only — the one path that drops references in bulk, and the one that
 * adds rows. `ordersCleared` (search unmount / Clear) and `orderUpdated` (the row leaves the
 * lists its new status moved it out of) deliberately do NOT prune: a detail is open over both,
 * and the next list response sweeps whatever they orphaned anyway.
 *
 * O(ids + rows), a few hundred either way: `byId` is bounded by the ten buckets after the first
 * pass, and the buckets are one server page each.
 */
function prune(state: OrdersV2State) {
  const referenced = new Set<string>();
  for (const storeKey of BUCKET_KEYS) for (const id of state.buckets[storeKey].ids) referenced.add(id);
  for (const id of Object.keys(state.byId)) if (!referenced.has(id)) delete state.byId[id];
}

const slice = createSlice({
  name: "ordersV2",
  initialState,
  reducers: {
    /**
     * v2's own list request — `storeKey` says which list (the spinner is per list) and the rest
     * IS the fetch: `buckets.listPayload`'s body and its two loader keys, which `myOrders/saga`
     * passes straight to the API. The reducer reads only the key.
     */
    ordersRequested: (state, action: PayloadAction<OrdersRequest>) => {
      const bucket = state.buckets[action.payload.storeKey];
      if (bucket) bucket.fetching = true;
    },
    /**
     * v2's own list response. `response` is the raw `sale/filter` body, exactly as v1 stores it,
     * so `{}` and `null` both read as an empty list — the offline lane hands over the same shape.
     */
    ordersReceived: (state, action: PayloadAction<{ storeKey: StoreKey; response: ListPayload }>) => {
      const { storeKey, response } = action.payload;
      const orders = response?.results ?? [];
      state.buckets[storeKey] = { ids: orders.map((order) => order.saleId), fetching: false };
      for (const order of orders) state.byId[order.saleId] = order;
      prune(state);
    },
    /** v2's own failure: the spinner stops, the stale ids stay (v1 keeps its stale list too). */
    ordersFailed: (state, action: PayloadAction<{ storeKey: StoreKey }>) => {
      const bucket = state.buckets[action.payload.storeKey];
      if (bucket) bucket.fetching = false;
    },
    /** v2's own clear for one list — the search view's unmount and its Clear button. */
    ordersCleared: (state, action: PayloadAction<{ storeKey: StoreKey }>) => {
      state.buckets[action.payload.storeKey] = { ids: [], fetching: false };
    },
    /**
     * One order's latest copy (a mutation's response, a detail fetch). The copy replaces what
     * `byId` held, and the row leaves every list the change moved it out of (`stillInList`). It is
     * NOT inserted into its destination list: that list is re-fetched when its tab is opened, and
     * a blind insert would guess the server's order. The overview needs neither:
     * `selectOverviewSplit` re-columns it from `byId`.
     */
    orderUpdated: (state, action: PayloadAction<SaleVO>) => {
      const order = action.payload;
      const saleId = order?.saleId;
      if (!saleId) return;
      const previous = state.byId[saleId];
      state.byId[saleId] = order;
      for (const storeKey of BUCKET_KEYS) {
        const bucket = state.buckets[storeKey];
        if (bucket.ids.includes(saleId) && !stillInList(storeKey, order, previous)) {
          bucket.ids = bucket.ids.filter((id) => id !== saleId);
        }
      }
    },
  },
});

export const { ordersRequested, ordersReceived, ordersFailed, ordersCleared, orderUpdated } = slice.actions;
export default slice.reducer;
